import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useOutletContext } from "react-router-dom";
import { motion, useSpring, useMotionValue, useTransform, MotionValue } from "framer-motion";
import "./Revenue.css";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip } from "recharts";
import { useLotAnalytics, useLotHourlyData, useLotDevices } from "../../hooks/useLotData";
import { useMultiLotAnalytics, useMultiLotHourlyData, useMultiLotDevices, useMultiLotDetails, useMultiLotOccupancy } from "../../hooks/useMultiLotData";
import { useMultiLot } from "../../contexts/MultiLotContext";
import { useLot } from "../../contexts/LotContext";
import { lotService } from "../../utils/api";
import { getCurrencyInfo } from "../../utils/currency";
import { parseDateAsUTC } from "../../utils/dateUtils";

interface DashboardMetrics {
  date: string;
  totalVehicles: number;
  averageOccupancy: number;
  upTime: number;
  totalRevenue: number;
  pendingRevenue?: number;
  subscriberRevenue?: number;
  currencyUsed?: string;
}

interface LotEntry {
  lotId: string;
  date: string;
  totalVehicles: string;
  averageOccupancy: string;
  upTime: string;
  totalRevenue: number;
  pendingRevenue: number;
  subscriberRevenue: number;
  currencyUsed?: string;
}

interface Device {
  deviceId: string;
  deviceStatus: string;
}

interface MetricProps {
  title: string;
  value: MotionValue<number>;
  prefix: string;
  suffix?: string;
  change?: number;
  prevValue?: MotionValue<number>;
  decimals: number;
  hideChange?: boolean;
  deviceStatus?: {
    online: number;
    offline: number;
    calibrating: number;
    recentlyAdded: number;
    onlineCount: number;
    offlineCount: number;
  };
  displayValue: MotionValue<string>;
  displayPrevValue?: MotionValue<string>;
  showProgressBar?: boolean;
  progressValue?: number;
}

interface DashboardContext {
  currentRange: { startDate: string; endDate: string };
  previousRange: { startDate: string; endDate: string };
  timeframe: "day" | "week" | "month" | "year";
}

interface HourlyData {
  timestamp: string;
  totalRevenue: number;
  pendingRevenue: number;
  subscriberRevenue?: number;
  totalVehicles: number;
  averageOccupancy: number;
  upTime: number;
  currencyUsed?: string;
}

const Revenue: React.FC = () => {
  const { lotId } = useParams<{ lotId: string }>();
  const { currentRange, previousRange, timeframe } = useOutletContext<DashboardContext>();
  const [filteredData, setFilteredData] = useState<LotEntry[]>([]);
  const [previousData, setPreviousData] = useState<LotEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [graphData, setGraphData] = useState<any[]>([]);
  const [hoveredData, setHoveredData] = useState<LotEntry | null>(null);
  const [lotName, setLotName] = useState<string>("...");
  const [hourlyData, setHourlyData] = useState<{ data: HourlyData[] } | null>(null);
  const [currentMetrics, setCurrentMetrics] = useState<DashboardMetrics[]>([]);
  const [currentOccupancy, setCurrentOccupancy] = useState<number | null>(null);
  const [lotCapacity, setLotCapacity] = useState<number | null>(null);
  const [currencyCode, setCurrencyCode] = useState<string | null>(null);
  const [isMixedCurrency, setIsMixedCurrency] = useState(false);

  // Multi-lot context
  const { isMultiLotMode, selectedLots, getSelectedLotIds } = useMultiLot();
  const { isDemoLot } = useLot();
  const lotIds = isMultiLotMode ? getSelectedLotIds() : [lotId || ""];

  // Single-lot hooks (used when not in multi-lot mode)
  const singleLotCurrentMetrics = useLotAnalytics(
    !isMultiLotMode ? (lotId || "") : "",
    currentRange.startDate,
    currentRange.endDate,
    timeframe
  );

  const singleLotPreviousMetrics = useLotAnalytics(
    !isMultiLotMode ? (lotId || "") : "",
    previousRange.startDate,
    previousRange.endDate,
    timeframe
  );

  const singleLotHourlyData = useLotHourlyData(
    !isMultiLotMode ? (lotId || "") : "",
    currentRange.startDate,
    timeframe
  );

  const singleLotDevices = useLotDevices(!isMultiLotMode ? (lotId || "") : "");

  // Multi-lot hooks (used when in multi-lot mode)
  const multiLotCurrentMetrics = useMultiLotAnalytics(
    isMultiLotMode ? lotIds : [],
    currentRange.startDate,
    currentRange.endDate,
    timeframe,
    selectedLots
  );

  const multiLotPreviousMetrics = useMultiLotAnalytics(
    isMultiLotMode ? lotIds : [],
    previousRange.startDate,
    previousRange.endDate,
    timeframe,
    selectedLots
  );

  const multiLotHourlyData = useMultiLotHourlyData(
    isMultiLotMode ? lotIds : [],
    currentRange.startDate,
    timeframe
  );

  const multiLotDevices = useMultiLotDevices(isMultiLotMode ? lotIds : []);
  const multiLotDetails = useMultiLotDetails(isMultiLotMode ? lotIds : []);
  const multiLotOccupancy = useMultiLotOccupancy(isMultiLotMode ? lotIds : []);

  // Select appropriate data based on mode
  const currentMetricsData = isMultiLotMode ? multiLotCurrentMetrics.data : singleLotCurrentMetrics.data;
  const isCurrentLoading = isMultiLotMode ? multiLotCurrentMetrics.isLoading : singleLotCurrentMetrics.isLoading;
  const isCurrentFetching = isMultiLotMode ? multiLotCurrentMetrics.isFetching : singleLotCurrentMetrics.isFetching;
  const currentError = isMultiLotMode
    ? (multiLotCurrentMetrics.errors?.find((err) => !!err) as Error | null) || (multiLotCurrentMetrics.isError ? new Error("Failed to load") : null)
    : (singleLotCurrentMetrics.error as Error | null);

  const previousMetricsData = isMultiLotMode ? multiLotPreviousMetrics.data : singleLotPreviousMetrics.data;
  const isPreviousLoading = isMultiLotMode ? multiLotPreviousMetrics.isLoading : singleLotPreviousMetrics.isLoading;
  const isPreviousFetching = isMultiLotMode ? multiLotPreviousMetrics.isFetching : singleLotPreviousMetrics.isFetching;
  const previousError = isMultiLotMode
    ? (multiLotPreviousMetrics.errors?.find((err) => !!err) as Error | null) || (multiLotPreviousMetrics.isError ? new Error("Failed to load") : null)
    : (singleLotPreviousMetrics.error as Error | null);

  const hourlyDataData = isMultiLotMode ? multiLotHourlyData.data : singleLotHourlyData.data;
  const isHourlyLoading = isMultiLotMode ? multiLotHourlyData.isLoading : singleLotHourlyData.isLoading;
  const isHourlyFetching = isMultiLotMode ? multiLotHourlyData.isFetching : singleLotHourlyData.isFetching;
  const hourlyError = isMultiLotMode
    ? (multiLotHourlyData.errors?.find((err) => !!err) as Error | null) || (multiLotHourlyData.isError ? new Error("Failed to load") : null)
    : (singleLotHourlyData.error as Error | null);

  const devicesData = isMultiLotMode ? multiLotDevices.data : singleLotDevices.data;
  const isDevicesLoading = isMultiLotMode ? multiLotDevices.isLoading : singleLotDevices.isLoading;
  const isDevicesFetching = isMultiLotMode ? multiLotDevices.isFetching : singleLotDevices.isFetching;
  const devicesError = isMultiLotMode
    ? (multiLotDevices.errors?.find((err) => !!err) as Error | null) || (multiLotDevices.isError ? new Error("Failed to load") : null)
    : (singleLotDevices.error as Error | null);

  // Check if we're in "today" view
  const isTodayView = useMemo(() => {
    if (timeframe !== "day") return false;
    const [startYear, startMonth, startDay] = currentRange.startDate.split('-').map(Number);
    const selectedDateObj = new Date(startYear, startMonth - 1, startDay);
    const now = new Date();
    const todayDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return selectedDateObj.getTime() === todayDate.getTime();
  }, [timeframe, currentRange.startDate]);

  const updateCurrencyState = useCallback((dailyData: LotEntry[], hourly?: HourlyData[]) => {
    const currencyValues = new Set<string>();
    const addCurrency = (value?: string) => {
      if (!value) return;
      const normalized = value.toUpperCase();
      currencyValues.add(normalized);
    };

    dailyData?.forEach((entry) => addCurrency(entry.currencyUsed));
    hourly?.forEach((entry) => addCurrency(entry.currencyUsed));

    if (currencyValues.size === 0) {
      setCurrencyCode(null);
      setIsMixedCurrency(false);
      return;
    }

    if (currencyValues.has("MIXED") || currencyValues.size > 1) {
      setCurrencyCode(null);
      setIsMixedCurrency(true);
      return;
    }

    setCurrencyCode(Array.from(currencyValues)[0] || null);
    setIsMixedCurrency(false);
  }, []);

  // Fetch lot capacity (or sum from multi-lot)
  useEffect(() => {
    const fetchLotCapacity = async () => {
      if (isMultiLotMode) {
        // Use multi-lot total capacity
        setLotCapacity(multiLotDetails.totalCapacity || null);
        return;
      }
      if (!lotId) return;
      try {
        const lotData = await lotService.getLotById(lotId);
        setLotCapacity(lotData.lotCapacity || null);
      } catch (error) {
        console.error("Error fetching lot capacity:", error);
        setLotCapacity(null);
      }
    };
    fetchLotCapacity();
  }, [lotId, isMultiLotMode, multiLotDetails.totalCapacity]);

  // Fetch current occupancy when in today view (or sum from multi-lot)
  useEffect(() => {
    const fetchCurrentOccupancy = async () => {
      if (isMultiLotMode) {
        // Use multi-lot total occupancy
        if (isTodayView && multiLotOccupancy.data !== null) {
          setCurrentOccupancy(multiLotOccupancy.data);
        } else {
          setCurrentOccupancy(null);
        }
        return;
      }
      if (!lotId || !isTodayView) {
        setCurrentOccupancy(null);
        return;
      }
      try {
        const occupancy = await lotService.getCurrentOccupancy(lotId);
        setCurrentOccupancy(occupancy);
      } catch (error) {
        console.error("Error fetching current occupancy:", error);
        setCurrentOccupancy(null);
      }
    };
    fetchCurrentOccupancy();
  }, [lotId, isTodayView, isMultiLotMode, multiLotOccupancy.data]);

  // Create animated values using useSpring directly - these must be at the top level
  const animatedRevenue = useSpring(0, { stiffness: 10000, damping: 600 });
  const animatedParkedCars = useSpring(0, { stiffness: 10000, damping: 600 });
  const animatedAvgOccupancy = useSpring(0, { stiffness: 10000, damping: 600 });
  const animatedUptime = useSpring(0, { stiffness: 10000, damping: 600 });
  const animatedPrevRevenue = useSpring(0, { stiffness: 10000, damping: 600 });
  const animatedPrevParkedCars = useSpring(0, { stiffness: 10000, damping: 600 });
  const animatedPrevAvgOccupancy = useSpring(0, { stiffness: 10000, damping: 600 });
  const animatedPrevUptime = useSpring(0, { stiffness: 10000, damping: 600 });

  // Create current occupancy display value
  const displayCurrentOccupancy = useMotionValue(
    currentOccupancy !== null && lotCapacity !== null ? `${currentOccupancy}/${lotCapacity}` : "0/0"
  );

  // Create dummy motion value for current occupancy metric
  const dummyCurrentOccupancyValue = useMotionValue(0);

  // Update current occupancy display when values change
  useEffect(() => {
    if (currentOccupancy !== null && lotCapacity !== null) {
      displayCurrentOccupancy.set(`${currentOccupancy}/${lotCapacity}`);
    }
  }, [currentOccupancy, lotCapacity, displayCurrentOccupancy]);

  // Create transform values at the top level with proper formatting
  const displayRevenue = useTransform(animatedRevenue, (latest: number) =>
    formatNumberWithK(latest)
  );
  const displayParkedCars = useTransform(animatedParkedCars, (latest: number) =>
    Number(latest).toLocaleString('en-US', { maximumFractionDigits: 0 })
  );
  const displayAvgOccupancy = useTransform(animatedAvgOccupancy, (latest: number) => {
    const value = isNaN(latest) ? 0 : latest;
    return value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  });
  const displayUptime = useTransform(animatedUptime, (latest: number) => {
    const value = isNaN(latest) ? 0 : latest;
    return value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  });
  const displayPrevRevenue = useTransform(animatedPrevRevenue, (latest: number) =>
    formatNumberWithK(latest)
  );
  const displayPrevParkedCars = useTransform(animatedPrevParkedCars, (latest: number) =>
    Number(latest).toLocaleString('en-US', { maximumFractionDigits: 0 })
  );
  const displayPrevAvgOccupancy = useTransform(animatedPrevAvgOccupancy, (latest: number) => {
    const value = isNaN(latest) ? 0 : latest;
    return value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  });
  const displayPrevUptime = useTransform(animatedPrevUptime, (latest: number) => {
    const value = isNaN(latest) ? 0 : latest;
    return value.toLocaleString('en-US', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  });

  // Process data when it changes
  useEffect(() => {
    if (!currentMetricsData || !previousMetricsData) return;

    console.log('Current Metrics Data:', currentMetricsData);
    console.log('Previous Metrics Data:', previousMetricsData);

    // Convert metrics to LotEntry format for display
    const convertedData: LotEntry[] = currentMetricsData.map((metric: DashboardMetrics) => {
      // Convert string values to numbers and ensure they're valid
      const avgOccupancy = parseFloat(metric.averageOccupancy.toString()) || 0;
      const upTime = parseFloat(metric.upTime.toString()) || 0;

      return {
        lotId: lotId || "",
        date: metric.date,
        totalVehicles: metric.totalVehicles.toString(),
        averageOccupancy: avgOccupancy.toString(),
        upTime: upTime.toString(),
        totalRevenue: metric.totalRevenue,
        pendingRevenue: metric.pendingRevenue || 0,
        subscriberRevenue: metric.subscriberRevenue || 0,
        currencyUsed: metric.currencyUsed
      };
    });

    const prevConvertedData: LotEntry[] = previousMetricsData.map((metric: DashboardMetrics) => {
      // Convert string values to numbers and ensure they're valid
      const avgOccupancy = parseFloat(metric.averageOccupancy.toString()) || 0;
      const upTime = parseFloat(metric.upTime.toString()) || 0;

      return {
        lotId: lotId || "",
        date: metric.date,
        totalVehicles: metric.totalVehicles.toString(),
        averageOccupancy: avgOccupancy.toString(),
        upTime: upTime.toString(),
        totalRevenue: metric.totalRevenue,
        pendingRevenue: metric.pendingRevenue || 0,
        subscriberRevenue: metric.subscriberRevenue || 0,
        currencyUsed: metric.currencyUsed
      };
    });

    setFilteredData(convertedData);
    setPreviousData(prevConvertedData);

    // Calculate metrics based on timeframe
    let totalRevenue = 0;
    let totalVehicles = 0;
    let avgOccupancy = 0;
    let avgUptime = 0;
    let previousRevenue = 0;
    let previousVehicles = 0;
    let previousOccupancy = 0;
    let previousUptime = 0;

    if (timeframe === 'day' && hourlyDataData?.data) {
      console.log('Hourly Data:', hourlyDataData.data);

      // For day view, calculate from hourly data
      const validHourlyData = hourlyDataData.data.filter((entry: HourlyData) => {
        const isValid = !isNaN(Number(entry.totalRevenue)) &&
          !isNaN(Number(entry.totalVehicles)) &&
          !isNaN(Number(entry.averageOccupancy)) &&
          !isNaN(Number(entry.upTime));

        if (!isValid) {
          console.log('Invalid hourly data entry:', entry);
        }
        return isValid;
      });

      if (validHourlyData.length > 0) {
        totalRevenue = validHourlyData.reduce((sum: number, entry: HourlyData) => sum + Number(entry.totalRevenue), 0);
        totalVehicles = validHourlyData.reduce((sum: number, entry: HourlyData) => sum + Number(entry.totalVehicles), 0);
        avgOccupancy = validHourlyData.reduce((sum: number, entry: HourlyData) => sum + Number(entry.averageOccupancy), 0) / validHourlyData.length;
        avgUptime = validHourlyData.reduce((sum: number, entry: HourlyData) => sum + Number(entry.upTime), 0) / validHourlyData.length;

        console.log('Calculated day metrics:', {
          totalRevenue,
          totalVehicles,
          avgOccupancy,
          avgUptime
        });
      }

      // For day view, get previous values from previousMetricsData
      if (previousMetricsData && previousMetricsData.length > 0) {
        const prevDayData = previousMetricsData[0];
        previousRevenue = Number(prevDayData.totalRevenue) || 0;
        previousVehicles = Number(prevDayData.totalVehicles) || 0;
        previousOccupancy = Number(prevDayData.averageOccupancy) || 0;
        previousUptime = Number(prevDayData.upTime) || 0;
      }
    } else {
      // For other timeframes, use daily data
      if (convertedData.length > 0) {
        totalRevenue = calculateSum(convertedData, "totalRevenue");
        totalVehicles = calculateSum(convertedData, "totalVehicles");
        avgOccupancy = calculateAvg(convertedData, "averageOccupancy");
        avgUptime = calculateAvg(convertedData, "upTime");

        console.log('Calculated timeframe metrics:', {
          totalRevenue,
          totalVehicles,
          avgOccupancy,
          avgUptime,
          dataLength: convertedData.length
        });
      }

      if (prevConvertedData.length > 0) {
        previousRevenue = calculateSum(prevConvertedData, "totalRevenue");
        previousVehicles = calculateSum(prevConvertedData, "totalVehicles");
        previousOccupancy = calculateAvg(prevConvertedData, "averageOccupancy");
        previousUptime = calculateAvg(prevConvertedData, "upTime");
      }
    }

    // Update animated values with proper percentage calculations
    animatedRevenue.set(totalRevenue);
    animatedParkedCars.set(totalVehicles);
    animatedAvgOccupancy.set(avgOccupancy); // Convert to percentage
    animatedUptime.set(avgUptime); // Don't multiply by 100 as uptime is already a percentage

    animatedPrevRevenue.set(previousRevenue);
    animatedPrevParkedCars.set(previousVehicles);
    animatedPrevAvgOccupancy.set(previousOccupancy); // Convert to percentage
    animatedPrevUptime.set(previousUptime); // Don't multiply by 100 as uptime is already a percentage

    // Set graph data based on timeframe
    if (timeframe === 'day' && hourlyDataData?.data) {
      // For day view, use hourly data
      const newGraphData = hourlyDataData.data.map((entry: HourlyData) => ({
        date: entry.timestamp,
        revenue: entry.totalRevenue,
        pendingRevenue: entry.pendingRevenue,
        subscriptions: entry.subscriberRevenue || 0
      }));
      setGraphData(newGraphData);
      // Dispatch graph data update
      window.dispatchEvent(new CustomEvent('graphDataUpdate', {
        detail: { graphData: newGraphData }
      }));
    } else {
      // For week/month/year views, use daily data
      const newGraphData = convertedData.map(entry => ({
        date: entry.date,
        revenue: entry.totalRevenue,
        pendingRevenue: entry.pendingRevenue,
        subscriptions: entry.subscriberRevenue
      }));
      setGraphData(newGraphData);
      // Dispatch graph data update
      window.dispatchEvent(new CustomEvent('graphDataUpdate', {
        detail: { graphData: newGraphData }
      }));
    }

    updateCurrencyState(convertedData, timeframe === 'day' && hourlyDataData?.data ? hourlyDataData.data : undefined);

  }, [currentMetricsData, previousMetricsData, hourlyDataData, timeframe, lotId, updateCurrencyState]);

  // Handle errors
  useEffect(() => {
    if (currentError || previousError || hourlyError || devicesError) {
      const errorMessage = (currentError || previousError || hourlyError || devicesError)?.message || "Failed to fetch dashboard data";
      setError(errorMessage);
    }
  }, [currentError, previousError, hourlyError, devicesError]);

  // Loading state
  const hasInitialData =
    !!currentMetricsData &&
    !!previousMetricsData &&
    (timeframe !== "day" || !!hourlyDataData);

  const isLoading = (
    isCurrentLoading ||
    isPreviousLoading ||
    isHourlyLoading ||
    isDevicesLoading ||
    isCurrentFetching ||
    isPreviousFetching ||
    isHourlyFetching ||
    isDevicesFetching
  ) && !hasInitialData;

  const calculateSum = (data: LotEntry[], key: keyof LotEntry): number => {
    if (!data || data.length === 0) return 0;
    return data.reduce((sum, entry) => {
      const value = Number(entry[key]);
      return sum + (isNaN(value) ? 0 : value);
    }, 0);
  };

  const calculateAvg = (data: LotEntry[], key: keyof LotEntry): number => {
    if (!data || data.length === 0) return 0;

    const validData = data.filter(entry => {
      const value = Number(entry[key]);
      return !isNaN(value) && value !== null && value !== undefined;
    });

    if (validData.length === 0) return 0;

    const sum = calculateSum(validData, key);
    const avg = sum / validData.length;

    // For percentage values, ensure we're working with the correct scale
    if (key === "averageOccupancy") {
      return isNaN(avg) ? 0 : parseFloat(avg.toFixed(1));
    }

    // For uptime, it's already a percentage, so return as is
    if (key === "upTime") {
      return isNaN(avg) ? 0 : parseFloat(avg.toFixed(1));
    }

    return isNaN(avg) ? 0 : parseFloat(avg.toFixed(1));
  };

  // Refine calculateChange to handle previousValue of 0 or negative gracefully
  const calculateChange = (current: number, previous: number): number => {
    if (previous === 0) {
      return current > 0 ? 100 : 0; // If previous was 0 and current is > 0, show 100% increase
    }
    return ((current - previous) / Math.abs(previous)) * 100;
  };

  // Calculate metrics - These calculations happen on every render. Ensure they use the latest state.
  // They are used to determine the 'change' and other derived values for the metrics array.
  const computedRevenue = useMemo(() => {
    if (timeframe === 'day' && hourlyDataData?.data && hourlyDataData.data.length > 0) {
      return hourlyDataData.data.reduce((sum: number, entry: HourlyData) => sum + Number(entry.totalRevenue), 0);
    }
    return filteredData.length > 0 ? calculateSum(filteredData, "totalRevenue") : 0;
  }, [timeframe, hourlyDataData, filteredData]);

  const computedPreviousRevenue = useMemo(() => {
    if (timeframe === 'day' && previousMetricsData && previousMetricsData.length > 0) {
      return Number(previousMetricsData[0].totalRevenue);
    }
    return previousData.length > 0 ? calculateSum(previousData, "totalRevenue") : 0;
  }, [timeframe, previousMetricsData, previousData]);

  const revenueChange = calculateChange(computedRevenue, computedPreviousRevenue);

  // New computed values for parked cars (total vehicles) and average occupancy using useMemo
  const computedVehicles = useMemo(() => {
    if (timeframe === 'day' && hourlyDataData?.data && hourlyDataData.data.length > 0) {
      return hourlyDataData.data.reduce((sum: number, entry: HourlyData) => sum + Number(entry.totalVehicles), 0);
    }
    return filteredData.length > 0 ? calculateSum(filteredData, "totalVehicles") : 0;
  }, [timeframe, hourlyDataData, filteredData]);

  const computedPreviousVehicles = useMemo(() => {
    if (timeframe === 'day' && previousMetricsData && previousMetricsData.length > 0) {
      return Number(previousMetricsData[0].totalVehicles);
    }
    return previousData.length > 0 ? calculateSum(previousData, "totalVehicles") : 0;
  }, [timeframe, previousMetricsData, previousData]);

  const vehiclesChange = calculateChange(computedVehicles, computedPreviousVehicles);

  const computedAvgOccupancy = useMemo(() => {
    if (timeframe === 'day' && hourlyDataData?.data && hourlyDataData.data.length > 0) {
      const total = hourlyDataData.data.reduce((sum: number, entry: HourlyData) => sum + Number(entry.averageOccupancy), 0);
      return total / hourlyDataData.data.length;
    }
    return filteredData.length > 0 ? calculateAvg(filteredData, "averageOccupancy") : 0;
  }, [timeframe, hourlyDataData, filteredData]);

  const computedPreviousAvgOccupancy = useMemo(() => {
    if (timeframe === 'day' && previousMetricsData && previousMetricsData.length > 0) {
      return Number(previousMetricsData[0].averageOccupancy);
    }
    return previousData.length > 0 ? calculateAvg(previousData, "averageOccupancy") : 0;
  }, [timeframe, previousMetricsData, previousData]);

  const occupancyChange = calculateChange(computedAvgOccupancy, computedPreviousAvgOccupancy);

  const totalSubscriberRevenue = calculateSum(filteredData, "subscriberRevenue");

  const avgUptime = filteredData.length > 0 ? calculateAvg(filteredData, "upTime") : 0;
  const previousUptime = previousData.length > 0 ? calculateAvg(previousData, "upTime") : 0;
  const uptimeChange = calculateChange(avgUptime, previousUptime);

  const pendingRevenue = filteredData.length > 0 ? filteredData[filteredData.length - 1].pendingRevenue : 0; // This might be 0 if not in hourly data

  const trendArrow = (change: number) => (change >= 0 ? "/assets/dashboard/DataUp.svg" : "/assets/dashboard/DataDown.svg");
  const getPreviousLabel = () => {
    switch (timeframe) {
      case "day":
        return "Previous Day";
      case "week":
        return "Previous Week";
      case "month":
        return "Previous Month";
      case "year":
        return "Previous Year";
      default:
        return "Previous Period";
    }
  };

  const getTrendTextClass = (change: number) => (change >= 0 ? "trend-text up" : "trend-text down");

  // Calculate online and offline devices from the cached data
  const deviceStatusCounts = devicesData ? devicesData.reduce((acc: {
    online: number;
    offline: number;
    calibrating: number;
    recentlyAdded: number;
    onlineCount: number;
    offlineCount: number;
  }, device: Device) => {
    if (isDemoLot && !isMultiLotMode) {
      acc.online++;
      acc.onlineCount++;
      return acc;
    }
    switch (device.deviceStatus) {
      case "Online":
        acc.online++;
        acc.onlineCount++;
        break;
      case "Calibrating":
        acc.calibrating++;
        acc.onlineCount++;
        break;
      case "RecentlyAdded":
        acc.recentlyAdded++;
        acc.offlineCount++;
        break;
      case "Offline":
      default:
        acc.offline++;
        acc.offlineCount++;
        break;
    }
    return acc;
  }, {
    online: 0,
    offline: 0,
    calibrating: 0,
    recentlyAdded: 0,
    onlineCount: 0,
    offlineCount: 0
  }) : {
    online: 0,
    offline: 0,
    calibrating: 0,
    recentlyAdded: 0,
    onlineCount: 0,
    offlineCount: 0
  };

  const currencyInfo = getCurrencyInfo(currencyCode || undefined);
  // Always use plain dollar sign for display; currency code stays in the label.
  const revenuePrefix = "$";
  const revenueTitle = isMixedCurrency ? "Revenue (Mixed)" : currencyInfo.code ? `Revenue (${currencyInfo.code})` : "Revenue";

  // Define metrics array (Ensure 'change' and 'prevValue' are correctly passed the calculated numbers and animated values)
  const metrics: MetricProps[] = [
    {
      title: revenueTitle,
      value: animatedRevenue,
      prefix: revenuePrefix,
      change: revenueChange,
      prevValue: animatedPrevRevenue,
      decimals: 2,
      displayValue: displayRevenue,
      displayPrevValue: displayPrevRevenue
    },
    {
      title: "Total Occupants",
      value: animatedParkedCars,
      prefix: "",
      change: vehiclesChange,
      prevValue: animatedPrevParkedCars,
      decimals: 0,
      displayValue: displayParkedCars,
      displayPrevValue: displayPrevParkedCars
    },
    // Conditionally show current occupancy or average occupancy
    ...(isTodayView && currentOccupancy !== null && lotCapacity !== null ? [
      {
        title: "Currently",
        value: dummyCurrentOccupancyValue, // Dummy value since we use displayValue
        prefix: "",
        suffix: "",
        hideChange: true,
        decimals: 0,
        displayValue: displayCurrentOccupancy,
        showProgressBar: true,
        progressValue: lotCapacity > 0 ? Math.min(100, Math.max(0, (currentOccupancy / lotCapacity) * 100)) : 0
      }
    ] : [
      {
        title: "Avg. Occupancy",
        value: animatedAvgOccupancy,
        prefix: "",
        suffix: "%",
        change: occupancyChange,
        prevValue: animatedPrevAvgOccupancy,
        decimals: 1,
        displayValue: displayAvgOccupancy,
        displayPrevValue: displayPrevAvgOccupancy
      }
    ]),
    {
      title: "Uptime",
      value: animatedUptime,
      prefix: "",
      suffix: "%",
      hideChange: true,
      deviceStatus: {
        online: deviceStatusCounts.online,
        offline: deviceStatusCounts.offline,
        calibrating: deviceStatusCounts.calibrating,
        recentlyAdded: deviceStatusCounts.recentlyAdded,
        onlineCount: deviceStatusCounts.onlineCount,
        offlineCount: deviceStatusCounts.offlineCount
      },
      decimals: 1,
      displayValue: displayUptime,
      displayPrevValue: displayPrevUptime
    }
  ];

  // Loading/empty state
  if (isLoading) {
    return (
      <div className="revenue-content">
        <div className="metrics-container revenue-skeleton-metrics">
          {Array.from({ length: 4 }).map((_, index) => (
            <div
              key={`metric-skeleton-${index}`}
              className="skeleton-block revenue-skeleton-card"
              aria-hidden="true"
            />
          ))}
        </div>

        <div className="graph-wrapper revenue-skeleton-graph">
          <div className="graph-section">
            <div className="skeleton-block revenue-skeleton-graph-block" aria-hidden="true" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="revenue-content">
        <div style={{ height: '320px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="revenue-content">
      <div className="metrics-container">
        {metrics.map((metric) => (
          <motion.div
            className="metric"
            key={metric.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <span className="metric-value">
              {metric.prefix}
              <motion.span>
                {metric.displayValue}
              </motion.span>
              {metric.suffix}
            </span>
            <span className="metric-title">{metric.title}</span>
            {metric.showProgressBar && metric.progressValue !== undefined ? (
              <div className="progress-bar-container">
                <div className="progress-bar-bg">
                  <div
                    className="progress-bar-fill"
                    style={{ width: `${metric.progressValue}%` }}
                  />
                </div>
                <span className="progress-bar-text">
                  This lot {(currentOccupancy !== null && lotCapacity !== null && lotCapacity > 0
                    ? Math.round((currentOccupancy / lotCapacity) * 100)
                    : 0)}% full
                </span>
              </div>
            ) : !metric.hideChange && metric.change !== undefined ? (
              <>
                <div className="trend-container">
                  <motion.img
                    src={trendArrow(metric.change)}
                    alt="trend"
                    className="trend-arrow"
                    animate={{ rotate: metric.change < 0 ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  />
                  <motion.div
                    className={getTrendTextClass(metric.change)}
                    key={`${metric.change}-${metric.title}`}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{
                      type: "spring",
                      stiffness: 500,
                      damping: 30,
                      mass: 1
                    }}
                  >
                    {metric.change.toFixed(2)}%
                  </motion.div>
                </div>
                <span className="previous-cycle">
                  {metric.prefix}
                  {metric.displayPrevValue && <motion.span>{metric.displayPrevValue}</motion.span>}
                  {metric.suffix} {getPreviousLabel()}
                </span>
              </>
            ) : metric.deviceStatus && (
              <div className="device-status">
                <div className="device-dots">
                  {Array(metric.deviceStatus.online).fill(0).map((_, i) => (
                    <span key={`online-${i}`} className="device-dot online" />
                  ))}
                  {Array(metric.deviceStatus.calibrating).fill(0).map((_, i) => (
                    <span key={`calibrating-${i}`} className="device-dot calibrating" />
                  ))}
                  {Array(metric.deviceStatus.recentlyAdded).fill(0).map((_, i) => (
                    <span key={`recent-${i}`} className="device-dot recently-added" />
                  ))}
                  {Array(metric.deviceStatus.offline).fill(0).map((_, i) => (
                    <span key={`offline-${i}`} className="device-dot offline" />
                  ))}
                </div>
                <span className="device-count">
                  {metric.deviceStatus.onlineCount} Online, {metric.deviceStatus.offlineCount} Offline
                </span>
              </div>
            )}
          </motion.div>
        ))}
      </div>

      {/* Graph Container */}
      <div className="graph-wrapper">
        <div className="graph-section">
          <ResponsiveContainer width="100%" height={window.innerWidth < 480 ? 200 : 350}>
            <AreaChart
              data={graphData}
              margin={{ top: 0, left: -60, right: 0, bottom: 5 }}
              onMouseMove={(e) => setHoveredData(e.activePayload?.[0]?.payload || null)}
              onMouseLeave={() => setHoveredData(null)}
            >
              <defs>
                <linearGradient id="revenueGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--parallel-blue)" stopOpacity={0.25} />
                  <stop offset="70%" stopColor="var(--parallel-blue)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="pendingGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--graph-yellow)" stopOpacity={0.25} />
                  <stop offset="90%" stopColor="var(--graph-yellow)" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="subscriptionsGradient" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="var(--graph-cyan)" stopOpacity={0.25} />
                  <stop offset="90%" stopColor="var(--graph-cyan)" stopOpacity={0} />
                </linearGradient>
              </defs>

              <CartesianGrid stroke="rgba(0, 0, 0, 0.07)" strokeDasharray="0" />

              <XAxis
                dataKey="date"
                orientation="top"
                tickMargin={10}
                interval={0}
                axisLine={false}
                tickLine={false}
                tick={(props) => {
                  const { x, y, payload, index } = props;
                  // For day view, use the raw timestamp
                  const date = timeframe === 'day' ? new Date(payload.value) : parseDateAsUTC(payload.value);

                  // For day view, show hours
                  if (timeframe === 'day') {
                    const hour = date.getHours();

                    // Only show labels every 3 hours on desktop, every 6 hours on mobile
                    const labelInterval = window.innerWidth < 480 ? 6 : 3;
                    if (hour % labelInterval !== 0) {
                      return <text x={x} y={y} textAnchor="middle" fill="var(--text-medium)" style={{ opacity: 0 }} />;
                    }

                    // Format the time as 9AM, 12AM, etc.
                    const formattedTime = date.toLocaleTimeString('en-US', {
                      hour: 'numeric',
                      hour12: true
                    });

                    // For the first tick, shift x by 10px and use 'start' anchor
                    // For the last tick, shift x by -10px and use 'end' anchor
                    // For all other ticks, use original x and 'middle' anchor
                    let adjustedX = x;
                    let textAnchor = 'middle';

                    if (index === 0) {
                      adjustedX = x + 10;
                      textAnchor = 'start';
                    } else if (index === graphData.length - 1) {
                      adjustedX = x - 10;
                      textAnchor = 'end';
                    }

                    return (
                      <text x={adjustedX} y={y} textAnchor={textAnchor} fill="var(--text-medium)">
                        {formattedTime}
                      </text>
                    );
                  }

                  // For week view, show short weekday names
                  if (timeframe === 'week') {
                    const weekday = date.toLocaleDateString('en-US', {
                      weekday: 'short',
                      timeZone: 'UTC'
                    });

                    // For the first tick, shift x by 10px and use 'start' anchor
                    // For the last tick, shift x by -20px and use 'end' anchor
                    // For all other ticks, use original x and 'middle' anchor
                    let adjustedX = x;
                    let textAnchor = 'middle';

                    if (index === 0) {
                      adjustedX = x + 10;
                      textAnchor = 'start';
                    } else if (index === graphData.length - 1) {
                      adjustedX = x - 10;
                      textAnchor = 'end';
                    }

                    return (
                      <text x={adjustedX} y={y} textAnchor={textAnchor} fill="var(--text-medium)">
                        {weekday}
                      </text>
                    );
                  }

                  // For month view, show week labels
                  if (timeframe === 'month') {
                    // Calculate which week this data point belongs to (1-based)
                    const weekNumber = Math.floor(index / 7) + 1;
                    const totalWeeks = Math.ceil(graphData.length / 7);

                    // Only show week labels at the start of each week
                    if (index % 7 !== 0) {
                      return <text x={x} y={y} textAnchor="middle" fill="var(--text-medium)" style={{ opacity: 0 }} />;
                    }

                    // For the first tick, shift x by 10px and use 'start' anchor
                    // For the last tick, shift x by -20px and use 'end' anchor
                    // For all other ticks, use original x and 'middle' anchor
                    let adjustedX = x;
                    let textAnchor = 'middle';

                    if (index === 0) {
                      adjustedX = x + 10;
                      textAnchor = 'start';
                    } else if (index === graphData.length - 1) {
                      adjustedX = x - 10;
                      textAnchor = 'end';
                    }

                    return (
                      <text x={adjustedX} y={y} textAnchor={textAnchor} fill="var(--text-medium)">
                        Week {weekNumber}
                      </text>
                    );
                  }

                  // For year view, show the date
                  if (timeframe === 'year') {
                    // Only show labels for the first day of each month
                    const day = date.getUTCDate();
                    if (day !== 1) {
                      return <text x={x} y={y} textAnchor="middle" fill="var(--text-medium)" style={{ opacity: 0 }} />;
                    }

                    // Format as short month name
                    const monthName = date.toLocaleDateString('en-US', {
                      month: 'short',
                      timeZone: 'UTC'
                    });

                    // For the first tick, shift x by 10px and use 'start' anchor
                    // For the last tick, shift x by -20px and use 'end' anchor
                    // For all other ticks, use original x and 'middle' anchor
                    let adjustedX = x;
                    let textAnchor = 'middle';

                    if (index === 0) {
                      adjustedX = x + 10;
                      textAnchor = 'start';
                    } else if (index === graphData.length - 1) {
                      adjustedX = x - 10;
                      textAnchor = 'end';
                    }

                    return (
                      <text x={adjustedX} y={y} textAnchor={textAnchor} fill="var(--text-medium)">
                        {monthName}
                      </text>
                    );
                  }

                  // For other timeframes (month), show the date
                  const formattedDate = date.toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC'
                  });

                  let adjustedX = x;
                  let textAnchor = 'middle';

                  if (index === 0) {
                    adjustedX = x + 10;
                    textAnchor = 'start';
                  } else if (index === graphData.length - 1) {
                    adjustedX = x - 10;
                    textAnchor = 'end';
                  }

                  return (
                    <text x={adjustedX} y={y} textAnchor={textAnchor} fill="var(--text-medium)">
                      {formattedDate}
                    </text>
                  );
                }}
              />

              <YAxis tick={false} axisLine={false} />
              <Tooltip content={() => null} cursor={{ stroke: "var(--text-medium)", strokeWidth: 1, strokeDasharray: "5 5" }} />

              <Area
                type="monotone"
                dataKey="revenue"
                stroke="var(--parallel-blue)"
                strokeWidth={2}
                fill="url(#revenueGradient)"
                fillOpacity={1}
                animationDuration={500}
                isAnimationActive={false}
                activeDot={{ r: 7, strokeWidth: 2, stroke: "var(--white)", fill: "var(--parallel-blue)" }}
              />

              <Area
                type="monotone"
                dataKey="pendingRevenue"
                stroke="var(--graph-yellow)"
                strokeWidth={2}
                fill="url(#pendingGradient)"
                fillOpacity={1}
                animationDuration={500}
                isAnimationActive={false}
                activeDot={{ r: 7, strokeWidth: 2, stroke: "white", fill: "var(--graph-yellow)" }}
              />

              <Area
                type="monotone"
                dataKey="subscriptions"
                stroke="var(--graph-cyan)"
                strokeWidth={2}
                fill="url(#subscriptionsGradient)"
                fillOpacity={1}
                animationDuration={500}
                isAnimationActive={false}
                activeDot={{ r: 7, strokeWidth: 2, stroke: "white", fill: "var(--graph-cyan)" }}
              />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        <div className="graph-metrics">
          <div className="graph-metrics-header">
            <span className="metric-date">
              {hoveredData ? (timeframe === 'day'
                ? new Date(hoveredData.date).toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true
                })
                : parseDateAsUTC(hoveredData.date).toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                  year: "numeric",
                  timeZone: "UTC"
                })
              ) : "Total"}
            </span>
          </div>

          {[
            { title: revenueTitle, key: "revenue", totalValue: computedRevenue, className: "" },
            { title: "Pending Revenue", key: "pendingRevenue", totalValue: pendingRevenue, className: "pending-revenue" },
            { title: "Subscriptions", key: "subscriptions", totalValue: totalSubscriberRevenue, className: "subscriber-revenue" },
          ].map(({ title, key, totalValue, className }) => {
            const latestEntry = filteredData.length > 0 ? filteredData[filteredData.length - 1] : null;
            const latestValue = latestEntry ? Number(latestEntry[key as keyof LotEntry]) || 0 : 0;
            const hoveredValue = hoveredData && key in hoveredData
              ? Number(hoveredData[key as keyof typeof hoveredData]) || 0
              : null;

            const displayValue = hoveredValue !== null ? hoveredValue : totalValue;
            const formattedValue = formatNumberWithK(displayValue);
            const [wholePart, decimalPart] = formattedValue.includes(".") ? formattedValue.split(".") : [formattedValue, "00"];

            return (
              <div className="metric graph-metric" key={title}>
                <span className={`metric-value ${className}`}>
                  {revenuePrefix}{wholePart}
                  {decimalPart && <span className="decimal">.{decimalPart}</span>}
                </span>
                <span className="metric-title">{title}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const formatDateOffset = (dateString: string, offsetDays: number) => {
  const date = parseDateAsUTC(dateString);
  date.setDate(date.getDate() + offsetDays);
  return date.toISOString().split('T')[0];
};

// Add helper function to format numbers with k suffix
const formatNumberWithK = (num: number): string => {
  if (num >= 10000) {
    // Convert to thousands and round to 1 decimal place
    const inK = (num / 1000).toFixed(1);
    // Remove trailing .0 if present
    return inK.endsWith('.0') ? inK.slice(0, -2) + 'k' : inK + 'k';
  }
  // For numbers less than 10000, format with commas and 2 decimal places
  return num.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
};

export default Revenue;
